import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, IconButton, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ImageSlider from '@components/UI/ImageSlider';
import { useWallBuilder } from '../state';
import { getSelectedTopStone, getSelectedEndStone, getSelectedTopCornerStone } from '../utils';
const ProductImageModal = ({ open, onClose, product, title }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { state } = useWallBuilder();
    const { selectedBlock, selectedTopStoneId, selectedEndStoneId, selectedTopCornerStoneId } = state;
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    // Convert the main product and other selected stones to ImageSlideData for the ImageSlider
    const images = useMemo(() => {
        if (!product)
            return [];
        const imgs = [];
        const seen = new Set(); // track by src to de-duplicate
        const pushImagesFromProduct = (p, contextTitle, contextDesc) => {
            var _a;
            // All images array first
            if (p.images && Array.isArray(p.images) && p.images.length > 0) {
                p.images.forEach((src) => {
                    if (!src || seen.has(src))
                        return;
                    seen.add(src);
                    imgs.push({
                        id: `${p.variant_id}-${src}`,
                        src,
                        alt: p.title,
                        thumbnail: src,
                        title: contextTitle || p.title,
                        description: contextDesc,
                        width: 500,
                        height: 500,
                    });
                });
            }
            // Featured media
            if (((_a = p.featured_media) === null || _a === void 0 ? void 0 : _a.src) && !seen.has(p.featured_media.src)) {
                seen.add(p.featured_media.src);
                imgs.push({
                    id: p.featured_media.id || `${p.variant_id}-featured`,
                    src: p.featured_media.src,
                    alt: p.featured_media.alt || p.title,
                    thumbnail: p.featured_media.src,
                    title: contextTitle || p.title,
                    description: contextDesc,
                    width: p.featured_media.width,
                    height: p.featured_media.height,
                });
            }
            // Fallback single image
            if (imgs.length === 0 && p.image && !seen.has(p.image)) {
                seen.add(p.image);
                imgs.push({
                    id: `${p.variant_id}-image`,
                    src: p.image,
                    alt: p.title,
                    thumbnail: p.image,
                    title: contextTitle || p.title,
                    description: contextDesc,
                    width: 400,
                    height: 400,
                });
            }
            // Variant images
            if (p.variants && Array.isArray(p.variants)) {
                p.variants.forEach((v) => {
                    var _a;
                    if (((_a = v.featured_media) === null || _a === void 0 ? void 0 : _a.src) && !seen.has(v.featured_media.src)) {
                        seen.add(v.featured_media.src);
                        imgs.push({
                            id: v.featured_media.id || `${v.variant_id}-featured`,
                            src: v.featured_media.src,
                            alt: v.featured_media.alt || v.title,
                            thumbnail: v.featured_media.src,
                            title: contextTitle || v.title,
                            description: contextDesc ? `${contextDesc} • Variant: ${v.title}` : `Variant: ${v.title}`,
                            width: v.featured_media.width,
                            height: v.featured_media.height,
                        });
                    }
                    else if (v.image && !seen.has(v.image)) {
                        seen.add(v.image);
                        imgs.push({
                            id: v.variant_id || `variant-${Math.random()}`,
                            src: v.image,
                            alt: v.title,
                            thumbnail: v.image,
                            title: contextTitle || v.title,
                            description: contextDesc ? `${contextDesc} • Variant: ${v.title}` : `Variant: ${v.title}`,
                            width: 400,
                            height: 400,
                        });
                    }
                });
            }
        };
        // 1) Main product images
        pushImagesFromProduct(product);
        // 2) Related/selected stones from the selected block in state
        if (selectedBlock) {
            // Top stone
            const topStone = getSelectedTopStone(selectedBlock, selectedTopStoneId);
            if (topStone)
                pushImagesFromProduct(topStone, `${topStone.title}`, 'Top stone');
            // End stone
            const endStone = getSelectedEndStone(selectedBlock, selectedEndStoneId);
            if (endStone)
                pushImagesFromProduct(endStone, `${endStone.title}`, 'End stone');
            // Corner stone
            if (selectedBlock.corner_stone) {
                pushImagesFromProduct(selectedBlock.corner_stone, `${selectedBlock.corner_stone.title}`, 'Corner stone');
            }
            // Top corner stone
            const topCorner = getSelectedTopCornerStone(selectedBlock, selectedTopCornerStoneId);
            if (topCorner)
                pushImagesFromProduct(topCorner, `${topCorner.title}`, 'Top corner stone');
        }
        // 3) If still empty, push placeholder
        if (imgs.length === 0) {
            imgs.push({
                id: 'placeholder',
                src: '/placeholder-image.png',
                alt: product.title,
                thumbnail: '/placeholder-image.png',
                title: product.title,
                width: 400,
                height: 400,
            });
        }
        return imgs;
    }, [product, selectedBlock, selectedTopStoneId, selectedEndStoneId, selectedTopCornerStoneId]);
    const handleSlideChange = useCallback((index) => {
        setSelectedImageIndex(index);
    }, []);
    const handleImageClick = useCallback((image, index) => {
        // Optional: Handle image click for fullscreen view or other actions
        console.log('Image clicked:', image, 'at index:', index);
    }, []);
    // Add keyboard navigation for closing the modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!open)
                return;
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };
        if (open) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [open, onClose]);
    if (!product) {
        return null;
    }
    return (_jsxs(Dialog, Object.assign({ open: open, onClose: onClose, maxWidth: 'lg', fullWidth: true, fullScreen: isMobile, PaperProps: {
            sx: {
                bgcolor: 'background.paper',
                maxHeight: isMobile ? '100vh' : '90vh',
            },
        } }, { children: [_jsxs(DialogTitle, Object.assign({ sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: 'h6', component: 'div', noWrap: true }, { children: title || product.title })), _jsx(IconButton, Object.assign({ "aria-label": 'close', onClick: onClose, sx: { color: 'text.secondary' } }, { children: _jsx(CloseIcon, {}) }))] })), _jsx(DialogContent, Object.assign({ sx: { p: 0, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' } }, { children: _jsx(Box, Object.assign({ sx: { margin: '5px' } }, { children: _jsx(ImageSlider, { images: images, height: isMobile ? '50vh' : '80vh', showThumbnails: images.length > 1, showNavigation: images.length > 1, showPagination: images.length > 1, onSlideChange: handleSlideChange, onImageClick: handleImageClick, ariaLabel: `${product.title} product images`, priority: true, loading: 'eager', displayTitle: true }) })) }))] })));
};
export default ProductImageModal;
